/**
 * cookieConsent — one source of truth for the visitor's cookie choice.
 *
 * The banner owns the choice, but other overlays need to read it: on narrow
 * screens the video-review popup and the cookie banner would land on top of
 * each other, so the popup holds off until the banner is gone. `storage`
 * events only fire in *other* tabs, so the choice is broadcast in-tab with a
 * custom event instead.
 */

import { safeLocalStorage } from "./safeStorage";

const STORAGE_KEY = "indexed_cookie_consent";
const CONSENT_EVENT = "indexed:cookie-consent";

export type CookieConsent = "accepted" | "declined";

// Incognito, blocked site data and in-app webviews all throw on localStorage,
// so every touch of it goes through safeLocalStorage — a storage failure must
// never break an overlay.
export function readConsent(): CookieConsent | null {
  const stored = safeLocalStorage.getItem(STORAGE_KEY);
  return stored === "accepted" || stored === "declined" ? stored : null;
}

export function writeConsent(choice: CookieConsent): void {
  // If the write is refused the broadcast below still happens, so overlays
  // waiting on the banner unblock for the rest of this pageview.
  safeLocalStorage.setItem(STORAGE_KEY, choice);
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: choice }));
  } catch {
    /* no CustomEvent — nothing else to do */
  }
}

/** Runs `fn` once the visitor answers the banner. Returns an unsubscribe. */
export function onConsentResolved(fn: () => void): () => void {
  const handler = () => fn();
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}
